import "./styles.less";

import { Card, Col, Row, Tag, Typography } from "antd";
import React, { useContext, useEffect, useState } from "react";

import { ConfigStateContext } from "../../../Contexts/Configurations/context";
import { Description } from "../Description/web";
import { Favorite } from "../Favorite/web";
import { LeadButtons } from "../InformationRequest/LeadButtons/web";
import { ListingTypeTag } from "../ListingTypeTag/web";
import { LocationTag } from "../LocationTag/web";
import { OwnerLogo } from "../OwnerLogo/web";
import { PageLink } from "../../Link/web";
import { PriceTag } from "../PriceTag/web";
import { PropComponentMode } from "../PropertyInterfaces";
import { SeenIcon } from "../SeenStatus/web";
import { Title } from "../Title/web";
import { TypologyTag } from "../TypologyTag/web";
import { usePropertyCard } from "./PropertyCard.hook";
import { useUser } from "../../User/User.hook";
import { CardImageGallery } from "./CardImageGallery/web";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { OnScreen } from "shared-components/Components/OnView/web";
import { useSeenStatus } from "../SeenStatus/SeenStatus.hook";
import { FacilitiesCard } from "../FacilitiesCard/web";
import { PropertyCardSkeleton } from "./PropertyCardSkeleton";

export type PropertyCardSizes = "default" | "large" | "mini";
export interface PropertyCardProps {
	id?: string;
	size?: PropertyCardSizes;
	hideActions?: boolean;
	staticGallery?: boolean;
	mode?: PropComponentMode;
	property: Listing;
	loading?: boolean;
	hideTags?: boolean;
	onPropertyClick?: () => void;
	onModalSimilarsCancelClick?: () => void;
}

export function PropertyCard({
	id,
	size = "default",
	hideActions = false,
	staticGallery = false,
	mode,
	property,
	onPropertyClick = null,
	onModalSimilarsCancelClick = null,
	loading = false,
	hideTags = false,
}: PropertyCardProps) {
	const listing = usePropertyCard({ property });
	const { user, isLoggedIn } = useUser();
	const { country_code } = useContext(ConfigStateContext);
	const { lg } = useBreakpoint();
	const { result } = useSeenStatus({ id });
	//const { filtersTags } = useFilters();
	const [hover, setHover] = useState(false);
	const [watchAgain, setWatchAgain] = useState(false);

	useEffect(() => {
		if (typeof result != "undefined" && !hover) setWatchAgain(!result);
	}, [result, hover]);

	if (loading) return <PropertyCardSkeleton />;

	const onHover = (e?: any) => {
		e?.preventDefault();
		if (!staticGallery) setHover(true);
	};
	const onHoverEnd = e => {
		e?.preventDefault();
		if (!staticGallery) setHover(false);
	};

	const cardExtra = (
		<Row className="property-card-top" justify={"space-between"} align={"top"}>
			{(country_code === "ce3" && listing.seaDistanceName !== null) && (
				<Tag color={"default"} style={{paddingLeft:25}}>
					<Typography.Text strong>{listing.seaDistanceName} {listing.seaDistanceName != "frente al mar" && "del mar"}</Typography.Text>
				</Tag>
			)}
			<Col flex={1}>{!hideTags && <ListingTypeTag id={id} />}</Col>
			<Col>
				<Favorite id={id} type={"justIcon"}  />
			</Col>
		</Row>
	);

	const cardTop = (
		<>
			{cardExtra}
			<PageLink
				isExternal={listing.isExternal}
				avoidRouter={listing.avoidRouter ?? false}
				pathname={listing.pathname}
				data={listing}
				as={listing.link}
				isBlank={true}
				callback={onPropertyClick || onModalSimilarsCancelClick}>
				<CardImageGallery
					id={id}
					idProject={listing.project.length > 0 ? listing.project[0].id : undefined}
					mode={mode}
					image={listing.img}
					youtube={listing.youtube}
					staticImage={staticGallery}
					hover={hover && (watchAgain || !isLoggedIn)}
				/>
			</PageLink>
			{country_code != "BR" && !!user.data?.me && !watchAgain && (
				<SeenIcon
					id={id}
					type={"overlay"}
					onWatchAgain={
						staticGallery
							? undefined
							: () => {
									setWatchAgain(true);
									setHover(true);
							  }
					}
				/>
			)}
		</>
	);

	const cardBottom = (
		<>
			{(size == "large" && country_code !== "ce3") && (
				<OwnerLogo
					logo={listing.owner.logo}
					name={listing.owner.name}
					inmoLink={listing.owner.inmoLink}
					inmoPropsLink={listing.owner.inmoPropsLink}
					loading={loading}
					inmoFull={listing.owner.inmofull}
				/>
			)}
			<PageLink
				isExternal={listing.isExternal}
				avoidRouter={listing.avoidRouter ?? false}
				pathname={listing.pathname}
				data={listing}
				as={listing.link}
				isBlank={true}
				callback={onPropertyClick || onModalSimilarsCancelClick}>
				<Row>
					<Col span={24}>
						<PriceTag
							commonExpenses={listing.commonExpenses}
							price={listing.price}
							isProject={listing.isProject}
							operationType={listing.operation_type}
							priceVariation={listing.price_variation}
							temporalCurrency={listing.temporal_currency}
							temporalPrice={listing.temporal_price}
						/>
					</Col>
					<Col span={24}>
						<TypologyTag
							propertyType={listing.property_type?.id}
							operationType={listing.operation_type?.id}
							bedrooms={listing.bedrooms}
							rooms={listing.rooms}
							bathrooms={listing.bathrooms}
							m2={listing.minM2 > 0 ? listing.minM2 : listing.m2}
							guests={listing.guests}
							loading={loading}
						/>
					</Col>
					<Col span={24}>
						<Title title={listing.title} hideInformation level={4} />
					</Col>
					{size == "large" && (
						<Col span={24}>
							<Description
								description={listing.description}
								hideInformation
								cleanHtml
							/>
						</Col>
					)}
					<Col span={24}>
						<LocationTag id={id} hideAddress />
					</Col>
					{country_code === "ce3" &&
						<Col xs={{span: 24}} sm={{span: 24}} md={{ span: size === "large" ? 12 : 24 }}>
							<FacilitiesCard facilities={listing.facilities}/>
						</Col>
					}
				</Row>
			</PageLink>
			{!hideActions && <LeadButtons id={id} mode={mode} sizeCard={size} isInListing />}
		</>
	);

	const card = (
		<Card
			size={"small"}
			cover={cardTop}
			className={`property-card property-card-${size} ${country_code}`}
			onMouseEnter={lg ? onHover : undefined}
			onMouseLeave={lg ? onHoverEnd : undefined}>
			{cardBottom}
		</Card>
	);

	return typeof lg != "undefined" && !lg ? (
		<OnScreen onEnterView={onHover} onLeaveView={onHoverEnd} delay={1500} threshold={0.5}>
			{card}
		</OnScreen>
	) : (
		card
	);
}

